import { Chip } from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import ValidationErrors from "../../../../components/ValidationErrors";

export default function ClaimDetailItemsTable({
  data,
  searchTerm = "",
  showStatus = true,
}) {
  const { t } = useTranslation();
  const transactionData =
    data?.getClaimTransaction?.data || data?.getClaimTransactionWithRef?.data;
  const allItems = transactionData?.items || [];

  const items = allItems.filter((item) => {
    if (!searchTerm) return true;
    const s = searchTerm.toLowerCase();
    return (
      item?.standard_code?.toLowerCase().includes(s) ||
      item?.standard_description?.toLowerCase().includes(s) ||
      item?.status?.toLowerCase().includes(s) ||
      item?.non_standard_code?.toLowerCase().includes(s) ||
      item?.non_standard_description?.toLowerCase().includes(s)
    );
  });
  const totalAmount = items.reduce((total, item) => {
    const net = Number(item?.net);
    return total + (Number.isFinite(net) ? net : 0);
  }, 0);
  const totalUnitPrice = items.reduce((total, item) => {
    const unitPrice = Number(item?.unit_price);
    return total + (Number.isFinite(unitPrice) ? unitPrice : 0);
  }, 0);
  const totalDiscount = items.reduce((total, item) => {
    const discount = Number(item?.discount_amount ?? item?.discount);
    return total + (Number.isFinite(discount) ? discount : 0);
  }, 0);
  const totalPatientShare = items.reduce((total, item) => {
    const patientShare = Number(item?.patient_share);
    return total + (Number.isFinite(patientShare) ? patientShare : 0);
  }, 0);
  const totalPayerShare = items.reduce((total, item) => {
    const payerShare = Number(item?.payer_share);
    return total + (Number.isFinite(payerShare) ? payerShare : 0);
  }, 0);

  const getStatusBadge = (status) => {
    const sl = status?.toLowerCase();
    const cls =
      sl === "approved" || sl === "active"
        ? "bg-green-100 text-green-800"
        : sl === "rejected" || sl === "denied"
          ? "bg-red-100 text-red-700"
          : "bg-gray-100 text-gray-700";
    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-md text-[13px] font-bold capitalize ${cls}`}
      >
        {status || "N/A"}
      </span>
    );
  };

  const infoLabels = [
    t("N-Std Code"),
    t("N-Std Desc"),
    t("Body Site"),
    t("Sub Site"),
    t("Is Package?"),
    t("Maternity?"),
    t("Start Date"),
    t("End Date"),
  ];

  const infoValues = (item) => [
    item?.non_standard_code,
    item?.non_standard_description,
    item?.body_site,
    item?.sub_site,
    item?.is_package ? t("Yes") : t("No"),
    item?.maternity ? t("Yes") : t("No"),
    item?.service_start_date,
    item?.service_end_date,
  ];

  const adjLabels = [
    t("Type"),
    t("Appr. Qty"),
    t("Benefit"),
    t("Discount"),
    t("Pat. Share"),
    t("Submitted"),
    t("Tax"),
  ];

  const adjValues = (item) => [
    item?.type || "—",
    item?.approved_quantity ?? item?.approved_value ?? item?.approved ?? "—",
    item?.benefit_value ?? item?.benefit ?? "0",
    item?.discount_amount ?? item?.discount ?? "0",
    item?.patient_share ?? "0",
    item?.submitted ?? "—",
    item?.tax_amount ?? item?.tax ?? "0",
  ];

  /* ─── Financial fields ─── */
  const finLabels = [
    t("Qty"),
    t("Unit Price"),
    t("Net"),
    t("Payer Share"),
    t("Factor"),
  ];

  const finValues = (item) => [
    item?.quantity ?? "0",
    item?.unit_price ?? "0",
    item?.net != null ? item.net.toFixed(2) : "0.00",
    item?.payer_share ?? "0",
    item?.factor ?? "0",
  ];

  const totalCols = 11;
  const hasValidation = (row) =>
    row?.has_validation_error === true ||
    (Array.isArray(row?.validation_errors) && row.validation_errors.length > 0);

  const groupTh = (extra = "") =>
    `px-2 py-1 text-center font-bold text-[10px] tracking-wider border-r border-gray-300 uppercase ${extra}`;
  const subTh = (extra = "") =>
    `px-2 py-1.5 border-r border-white/40 text-left leading-tight whitespace-nowrap text-[10px] font-[700] uppercase tracking-tight text-[#1e293b] ${extra}`;
  const tdBase = (extra = "") =>
    `px-2.5 py-1.5 border-r border-gray-200 align-top ${extra}`;
  const tdStacked = (extra = "") =>
    `p-0 border-r border-gray-200 align-top h-full ${extra}`;

  const LabelCol = ({ labels }) => (
    <div className="flex flex-col h-full w-full">
      {labels.map((lbl, i) => (
        <div
          key={i}
          className="flex items-center min-h-[28px] px-2.5 text-gray-500 text-[11px] font-semibold whitespace-nowrap leading-tight border-b border-gray-200"
        >
          {lbl}:
        </div>
      ))}
    </div>
  );

  const ValueCol = ({ values, blueIdxs = [] }) => (
    <div className="flex flex-col h-full w-full">
      {values.map((val, i) => (
        <div
          key={i}
          className={`flex items-center min-h-[28px] px-2.5 text-[11px] font-bold leading-tight border-b border-gray-200 ${
            blueIdxs.includes(i) ? "text-[#0070C2]" : "text-gray-800"
          }`}
        >
          {val ?? "—"}
        </div>
      ))}
    </div>
  );

  return (
    <div className="w-full overflow-hidden border border-gray-300 rounded-[6px]">
      <div className="overflow-x-auto">
        <table className="w-full text-xs border-collapse">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#F4F7FB] text-[#475569] border-b border-gray-300 uppercase">
              <th colSpan={1} className={groupTh("w-[3%] text-[12px]")}>
                #
              </th>
              <th colSpan={1} className={groupTh("text-[12px]")}>
                {t("Item")}
              </th>
              <th colSpan={2} className={groupTh("text-[12px]")}>
                {t("Adjudication")}
              </th>
              <th colSpan={2} className={groupTh("text-[12px]")}>
                {t("Info")}
              </th>
              <th colSpan={2} className={groupTh("text-[12px]")}>
                {t("Financials")}
              </th>
              <th
                colSpan={3}
                className="px-2 py-1 text-center font-bold text-[12px] tracking-wider uppercase"
              >
                {t("Linked Data")}
              </th>
            </tr>

            {/* ════ SUB HEADER ════ */}
            <tr className="bg-[#d1d5db]">
              <th className={subTh("text-center w-[3%] text-[12px]")}>
                {t("Seq.")}
              </th>

              {/* ITEM */}
              <th className={subTh("w-[14%] text-[12px]")}>{t("Item")}</th>

              {/* ADJUDICATION – Labels col | Values col */}
              <th className={subTh("w-[7%] text-gray-800 text-[12px]")}>
                {t("Category")}
              </th>
              <th className={subTh("w-[9%] text-gray-800 text-[12px]")}>
                {t("Values")}
              </th>

              {/* INFO – Labels col | Values col */}
              <th className={subTh("w-[8%] text-gray-800 text-[12px]")}>
                {t("Labels")}
              </th>
              <th className={subTh("w-[12%] text-gray-800 text-[12px]")}>
                {t("Values")}
              </th>

              {/* FINANCIALS – Labels col | Values col */}
              <th className={subTh("w-[8%] text-gray-800 text-[12px]")}>
                {t("Labels")}
              </th>
              <th className={subTh("w-[10%] text-gray-800 text-[12px]")}>
                {t("Values")}
              </th>

              {/* LINKED DATA */}
              <th className={subTh("w-[8%] text-[12px]")}>{t("Diagnosis")}</th>
              <th className={subTh("w-[8%] text-[12px]")}>
                {t("Care Teams")}
              </th>
              <th className="px-2 py-1.5 text-left leading-tight whitespace-nowrap text-[12px] font-[700] uppercase tracking-tight text-[#1e293b]">
                {t("Supp. Info")}
              </th>
            </tr>
          </thead>

          {/* ════ BODY ════ */}
          <tbody className="bg-white">
            {items.length === 0 ? (
              <tr>
                <td
                  colSpan={totalCols}
                  className="px-4 py-4 text-center text-gray-400 italic"
                >
                  {t("No items found")}
                </td>
              </tr>
            ) : (
              items.map((item, iIdx) => {
                const diagnosisCodes =
                  transactionData?.diagnosis?.filter((_, idx) =>
                    item?.diagnosis?.includes(idx + 1),
                  ) || [];
                const careTeams =
                  transactionData?.care_teams?.filter(
                    (_, idx) => item?.care_teams?.includes(idx + 1),
                  ) || [];
                const suppInfo =
                  transactionData?.supporting_info?.filter(
                    (info) => item?.supporting_info?.includes(info.sequence_no),
                  ) || [];

                return (
                  <React.Fragment key={iIdx}>
                    <tr
                      className="border-b border-gray-200 hover:bg-gray-50/50 transition-colors"
                      style={{
                        backgroundColor: hasValidation(item) ? "#FEF3C7" : "transparent",
                      }}
                    >
                      <td className={`${tdBase()} text-center font-bold text-[10px] text-gray-500`}>
                        {iIdx + 1}
                      </td>

                      <td className={tdBase()}>
                        <span className="font-bold text-[#0070C2] text-[12px] leading-tight block mb-1">
                          {item?.standard_code || "—"}
                        </span>
                        <div className="font-medium text-[#1e293b] text-[11px] leading-tight mb-2">
                          {item?.standard_description || "—"}
                        </div>
                        {showStatus && (
                          <div className="mt-2.5 flex flex-col gap-1.5">
                            <div className="flex items-center gap-1.5">
                              <span className="text-gray-500 text-[13px] font-bold">
                                {t("Status")}:
                              </span>
                              {getStatusBadge(item?.status)}
                            </div>
                            {item?.reject_reason_display && (
                              <div className="flex items-start gap-1.5 mt-1">
                                <span className="text-red-600 text-[11px] font-bold whitespace-nowrap">
                                  {t("Reject Reason")}:
                                </span>
                                <span className="text-red-700 text-[11px] font-medium leading-tight">
                                  {item.reject_reason_code && `[${item.reject_reason_code}] `}
                                  {item.reject_reason_display}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </td>

                      <td className={tdStacked("bg-gray-50/50")}>
                        <LabelCol labels={adjLabels} />
                      </td>

                      <td className={tdStacked()}>
                        <ValueCol values={adjValues(item)} />
                      </td>

                      <td className={tdStacked("bg-gray-50/50")}>
                        <LabelCol labels={infoLabels} />
                      </td>

                      <td className={tdStacked()}>
                        <ValueCol values={infoValues(item)} />
                      </td>

                      <td className={tdStacked("bg-gray-50/50")}>
                        <LabelCol labels={finLabels} />
                      </td>

                      <td className={tdStacked()}>
                        <ValueCol
                          values={finValues(item)}
                          blueIdxs={[2]} 
                        />
                      </td>

                      <td className={tdStacked()}>
                        <div className="flex flex-col h-full w-full">
                          {diagnosisCodes.length > 0 ? (
                            diagnosisCodes.map((d, i) => (
                              <div
                                key={i}
                                className="flex items-center min-h-[28px] px-2.5 border-b border-gray-200"
                              >
                                <span className="inline-block bg-blue-50 text-[#0070C2] px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap border border-blue-100">
                                  {d?.code}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center min-h-[28px] px-2.5 border-b border-gray-200">
                              <span className="text-gray-300 text-[10px]">
                                —
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className={tdStacked()}>
                        <div className="flex flex-col h-full w-full">
                          {careTeams.length > 0 ? (
                            careTeams.map((team, i) => (
                              <div
                                key={i}
                                className="flex items-center min-h-[28px] px-2.5 border-b border-gray-200"
                              >
                                <span className="inline-block bg-green-50 text-green-700 px-1.5 py-0.5 rounded text-[11px] font-medium whitespace-nowrap border border-green-100">
                                  {team?.name}
                                </span>
                              </div>
                            ))
                          ) : (
                            <div className="flex items-center min-h-[28px] px-2.5 border-b border-gray-200">
                              <span className="text-gray-300 text-[10px]">
                                —
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      <td className="px-2.5 py-1.5 align-top">
                        <div className="flex flex-wrap gap-0.5">
                          {suppInfo.length > 0 ? (
                            suppInfo.map((info, idx) => (
                              <Chip
                                key={idx}
                                label={info.category.replace(/-/g, " ")}
                                size="small"
                                variant="outlined"
                                sx={{
                                  fontSize: "11px",
                                  textTransform: "capitalize",
                                  height: 18,
                                }}
                              />
                            ))
                          ) : (
                            <span className="text-gray-300 text-[11px]">—</span>
                          )}
                        </div>
                      </td>
                    </tr>
                    {hasValidation(item) && (
                      <tr>
                        <td colSpan={totalCols} className="!p-0">
                          <ValidationErrors
                            validationErrors={item.validation_errors}
                            compact
                          />
                        </td>
                      </tr>
                    )}

                    {iIdx < items.length - 1 && (
                      <tr className="h-1 bg-gray-100">
                        <td
                          colSpan={totalCols}
                          className="p-0 border-y border-gray-300"
                        />
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
              )}
          </tbody>
        </table>
      </div>
      {items.length > 0 ? (
        <div className="flex flex-col gap-2 border-t border-gray-300 bg-gray-100 ps-3 py-0 md:flex-row md:items-center md:justify-between">
          <div className="text-[13px] font-semibold text-gray-800">
            {t("Totals")}
          </div>
          <div className="grid w-full grid-cols-1 overflow-hidden rounded border border-gray-200 bg-white md:w-auto md:min-w-[980px] md:grid-cols-5">
            <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-3 py-2 md:border-b-0 md:border-r">
              <span className="text-[12px] font-medium text-gray-600">
                {t("Unit Price Total")}
              </span>
              <span className="text-[12px] font-semibold text-gray-800">
                SAR {totalUnitPrice.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-3 py-2 md:border-b-0 md:border-r">
              <span className="text-[12px] font-medium text-gray-600">
                {t("Discount Total")}
              </span>
              <span className="text-[12px] font-semibold text-gray-800">
                SAR {totalDiscount.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-3 py-2 md:border-b-0 md:border-r">
              <span className="text-[12px] font-medium text-gray-600">
                {t("Patient Share Total")}
              </span>
              <span className="text-[12px] font-semibold text-gray-800">
                SAR {totalPatientShare.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 border-b border-gray-200 px-3 py-2 md:border-b-0 md:border-r">
              <span className="text-[12px] font-medium text-gray-600">
                {t("Payer Share Total")}
              </span>
              <span className="text-[12px] font-semibold text-gray-800">
                SAR {totalPayerShare.toFixed(2)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4 bg-blue-50 px-3 py-2">
              <span className="text-[12px] font-semibold text-[#1d4ed8]">
                {t("Total Net")}
              </span>
              <span className="text-[13px] font-bold text-[#0070C2]">
                SAR {totalAmount.toFixed(2)}
              </span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
